import { css } from "@emotion/react"
import { Button } from "matsuri-ui"
import CloseIcon from "@mui/icons-material/Close"
import { useState } from "react"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"

// フッターの主ボタンに被らないよう、スロットの上端より上へ吹き出しを浮かせる。
// started/finished ではフッターが主ボタンと横並びのため、上方向に逃がさないと覆ってしまう。
const bubbleContainerStyle = css`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
`

const bubbleButtonStyle = css`
    z-index: ${theme.zIndex.speedDial};
    background: ${theme.palette.intentions.cleanerGray.light};
    border: none;
    border-bottom-left-radius: 0;
    box-shadow: ${theme.shadows[5]};

    animation-name: fadeIn;
    animation-duration: 200ms;
    animation-fill-mode: both;

    @keyframes fadeIn {
        0% {
            opacity: 0;
            transform: translateY(4px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }
`

/**
 * 清掃詳細のフッターに常時出す報告導線。
 *
 * 忘れ物報告の対象エリア（`lostItemEnabled`）のときは、最初は1つの吹き出し
 * 「トラブルまたは忘れ物がありましたか？」を出し、タップでトラブル / 忘れ物の2択に開く
 * （閉じると元の1つに戻せる）。
 * 対象外・判定前は従来通りトラブル報告への吹き出し1つだけを出す。
 */
export const ToTroubleReportButton = ({
    toTroubleReportLink,
    toLostItemReportLink,
    lostItemEnabled = false
}: {
    toTroubleReportLink: string
    toLostItemReportLink: string
    lostItemEnabled?: boolean
}) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)

    // 忘れ物報告の対象外・判定前は、忘れ物を出さず従来のトラブルだけの吹き出しに戻す。
    if (!lostItemEnabled) {
        return (
            <div
                css={css`
                    position: relative;
                `}
            >
                <div css={bubbleContainerStyle}>
                    <Button
                        as="a"
                        href={toTroubleReportLink}
                        rounded
                        variant="outlined"
                        css={bubbleButtonStyle}
                    >
                        {t("トラブルがありましたか？")}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div
            css={css`
                position: relative;
            `}
        >
            <div css={bubbleContainerStyle}>
                {expanded ? (
                    <>
                        <Button
                            as="a"
                            href={toTroubleReportLink}
                            rounded
                            variant="outlined"
                            css={bubbleButtonStyle}
                        >
                            {t("トラブルを報告する")}
                        </Button>
                        <Button
                            as="a"
                            href={toLostItemReportLink}
                            rounded
                            variant="outlined"
                            css={bubbleButtonStyle}
                        >
                            {t("忘れ物を報告する")}
                        </Button>
                        {/* じゃまにならないよう、閉じて元の1つの吹き出しに戻す */}
                        <Button
                            rounded
                            variant="outlined"
                            onClick={() => setExpanded(false)}
                            css={bubbleButtonStyle}
                        >
                            <CloseIcon
                                css={css`
                                    margin-right: 4px;
                                    font-size: 18px;
                                `}
                            />
                            {t("閉じる")}
                        </Button>
                    </>
                ) : (
                    <Button
                        rounded
                        variant="outlined"
                        onClick={() => setExpanded(true)}
                        css={bubbleButtonStyle}
                    >
                        {t("トラブルまたは忘れ物がありましたか？")}
                    </Button>
                )}
            </div>
        </div>
    )
}
